import { useState } from 'react';
import PersonalInfoCard from '../02-BUSINESS-CARD/PersonalInfoCard';
import InvestmentZonesMap from '../03-MAP/InvestmentZonesMap';
import InvestmentStrategiesSelector from '../04-STRATEGIES/InvestmentStrategiesSelector';
import StrengthsWeaknesses from '../05-EVAL/StrengthsWeaknesses';
import useUserData from '../hooks-back/useUserData';

const ProfilePage = () => {
    // Hook centralisé pour toutes les données utilisateur
    const {
        data,
        loading,
        errors,
        updateContactData,
        updatePreferencesData,
        refreshData,
        isAuthenticated,
        hasUnsavedChanges
    } = useUserData();

    // États locaux pour l'UI seulement (pas de données métier)
    const [similarProfilesCount, setSimilarProfilesCount] = useState(0);

    // Handlers pour les composants enfants
    const handleContactChange = (field, value) => {
        updateContactData({ [field]: value });
    };

    const handleContactBulkChange = (updates) => {
        updateContactData(updates);
    };

    const handleVisibilityChange = (visible) => {
        updateContactData({ is_visible: visible });
    };

    const handleZonesChange = (newZones) => {
        updatePreferencesData({ fav_departments: newZones });
    };

    const handleStrategiesChange = (newStrategies) => {
        updatePreferencesData({ fav_strategies: newStrategies });
    };

    const handleStrengthsChange = (newStrengths) => {
        updatePreferencesData({ user_atouts: newStrengths });
    };

    const handleWeaknessesChange = (newWeaknesses) => {
        updatePreferencesData({ user_besoins: newWeaknesses });
    };

    // Recherche de profils similaires (logique métier à implémenter)
    const handleFindSimilarProfiles = () => {
        // TODO: Implémenter la recherche via API
        // Cette fonction pourrait utiliser les données actuelles pour faire une recherche
        setSimilarProfilesCount(prev => prev + Math.floor(Math.random() * 5));
    };

    // Affichage d'erreur globale si non authentifié
    if (!isAuthenticated) {
        return (
            <div className="relative h-screen flex items-center justify-center bg-gray-50">
                <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-lg text-center">
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">
                        Authentification requise
                    </h2>
                    <p className="text-gray-600">
                        Veuillez vous connecter pour accéder à votre profil.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="relative" style={{ height: '100vh' }}>
            {/* Indicateur global de sauvegarde en cours */}
            {loading.saving && (
                <div className="absolute top-4 right-4 z-50 bg-blue-100 border border-blue-200 rounded-lg px-4 py-2 shadow-lg">
                    <div className="flex items-center text-blue-800">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        <span className="text-sm font-medium">Sauvegarde en cours...</span>
                    </div>
                </div>
            )}

            {/* Indicateur d'erreur globale */}
            {errors.saving && (
                <div className="absolute top-4 right-4 z-50 bg-red-100 border border-red-200 rounded-lg px-4 py-2 shadow-lg">
                    <div className="flex items-center text-red-800">
                        <div className="w-4 h-4 rounded-full bg-red-500 mr-2 flex-shrink-0"></div>
                        <span className="text-sm font-medium">Erreur de sauvegarde</span>
                    </div>
                </div>
            )}

            {/* Section principale en grille 2x2 */}
            <div className="grid grid-cols-2 gap-6 h-full">
                {/* Colonne gauche */}
                <div className="flex flex-col gap-6">
                    {/* Informations Personnelles */}
                    <div style={{ height: '600px' }}>
                        <PersonalInfoCard
                            // Données de contact
                            contactData={data.contact}
                            
                            // États de chargement et erreurs
                            loading={loading.contact}
                            error={errors.contact}
                            saving={loading.saving}
                            
                            // Handlers pour les modifications
                            onContactChange={handleContactChange}
                            onContactBulkChange={handleContactBulkChange}
                            onVisibilityChange={handleVisibilityChange}
                            
                            // Props UI existantes
                            isProfileVisible={data.contact.is_visible}
                            similarProfilesCount={similarProfilesCount}
                            onFindSimilarProfiles={handleFindSimilarProfiles}
                        />
                    </div>

                    {/* Points Forts / Points Faibles */}
                    <div style={{ height: '450px' }}>
                        <StrengthsWeaknesses
                            // Données
                            canOffer={data.preferences.user_atouts}
                            seeking={data.preferences.user_besoins}
                            
                            // États
                            loading={loading.preferences}
                            error={errors.preferences}
                            saving={loading.saving}
                            
                            // Handlers
                            onStrengthsChange={handleStrengthsChange}
                            onWeaknessesChange={handleWeaknessesChange}
                        />
                    </div>
                </div>

                {/* Colonne droite */}
                <div className="flex flex-col gap-6">
                    {/* Carte des Zones d'Intervention */}
                    <div style={{ height: '600px' }}>
                        <InvestmentZonesMap
                            // Données
                            selectedZones={data.preferences.fav_departments}
                            
                            // États
                            loading={loading.preferences}
                            error={errors.preferences}
                            saving={loading.saving}
                            
                            // Handlers
                            onZonesChange={handleZonesChange}
                        />
                    </div>

                    {/* Sélecteur de Stratégies */}
                    <div style={{ height: '450px' }}>
                        <InvestmentStrategiesSelector
                            // Données
                            selectedStrategies={data.preferences.fav_strategies}
                            
                            // États
                            loading={loading.preferences}
                            error={errors.preferences}
                            saving={loading.saving}
                            
                            // Handlers
                            onStrategiesChange={handleStrategiesChange}
                        />
                    </div>
                </div>
            </div>

            {/* Debug info en développement */}
            {process.env.NODE_ENV === 'development' && (
                <div className="absolute bottom-4 left-4 bg-gray-900 text-white text-xs p-2 rounded opacity-50">
                    <div>Chargement: {JSON.stringify(loading)}</div>
                    <div>Changements non sauvés: {hasUnsavedChanges ? 'Oui' : 'Non'}</div>
                </div>
            )}
        </div>
    );
};

export default ProfilePage;